import { Box, Typography, Button, Alert } from '@mui/material'
import { useState, useEffect, useRef } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { DialogBox } from '~pages/pages-behind-login/chartering/pre-fixture-page/components/dialog-box'
import { UnsavedChangesCloseDialog } from '~pages/pages-behind-login/chartering/call-log-page/components/unsaved-changes-close-dialog'
import { usePatchPortInfo } from '~hooks/queries/port-info/use-patch-port-info'
import { useDeletePortInfo } from '~hooks/queries/port-info/use-delete-port-info'
import { useGetPortInfoById } from '~hooks/queries/port-info/use-get-port-info'
import type { PortInfoItem, PortInfoFormData, PortSelection } from '../../types'
import { PageTwo } from '../add-visit/sub-components/page-two/page-two'
import {
  buildEditPatchPayload,
  getMissingRequiredFields,
} from '../../utils/form-utils'
import { COLORS } from '~theme/colors'
import { formatDate } from '~utils/dates'
import { useDateFormat } from '~hooks/use-date-format'

const modalSlotProps = {
  paper: { sx: { width: '90vw', maxWidth: '2000px' } },
}

const DATE_FIELDS = new Set(['dateOfArrival', 'dateOfDeparture'])
const SKIP_FIELDS = new Set(['portName', 'portCode', 'portCountry'])

function setFormDataField(
  formData: PortInfoFormData,
  key: string,
  value: unknown,
): void {
  const formKey = key as keyof PortInfoFormData
  ;(formData as Record<string, unknown>)[formKey] = value
}

function portInfoToFormData(visit: PortInfoItem): PortInfoFormData {
  const formData: PortInfoFormData = {}

  for (const [key, value] of Object.entries(visit)) {
    if (SKIP_FIELDS.has(key)) continue
    if (value === undefined || value === null) continue

    if (DATE_FIELDS.has(key)) {
      const dateKey = key as 'dateOfArrival' | 'dateOfDeparture'
      formData[dateKey] = new Date(value as string)
    } else {
      setFormDataField(formData, key, value)
    }
  }

  formData.port = {
    port: {
      PortName: visit.portName,
      PortCode: visit.portCode,
      CountryName: visit.portCountry,
      CountryCode: '',
      ContinentName: '',
      ContinentCode: '',
    },
    portName: visit.portName,
  } as PortSelection

  return formData
}

interface EditVisitModalProps {
  visitId: string
  onClose: () => void
}

export function EditVisitModal({ visitId, onClose }: EditVisitModalProps) {
  const [formData, setFormData] = useState<PortInfoFormData>({})
  const [showUnsavedCloseConfirm, setShowUnsavedCloseConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [missingFields, setMissingFields] = useState<
    (keyof PortInfoFormData)[]
  >([])
  const initialFormDataRef = useRef<PortInfoFormData | null>(null)
  const { setStatus } = useAnimatedAlert()
  const dateFormat = useDateFormat('with hours and minutes')

  const { data: visit, isLoading: isLoadingVisit } = useGetPortInfoById(visitId)

  const {
    mutate: patchVisit,
    isPending: isPendingPatch,
    isError: isErrorPatch,
  } = usePatchPortInfo()

  const { mutate: deleteVisit, isPending: isPendingDelete } =
    useDeletePortInfo()

  useEffect(() => {
    if (visit) {
      const initialData = portInfoToFormData(visit)
      setFormData(initialData)
      initialFormDataRef.current = initialData
    }
  }, [visit])

  function hasUnsavedChanges(): boolean {
    if (!initialFormDataRef.current) return false
    return (
      JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current)
    )
  }

  function requestClose() {
    if (hasUnsavedChanges() && !showUnsavedCloseConfirm) {
      setShowUnsavedCloseConfirm(true)
      return
    }
    onClose()
  }

  function onSave() {
    if (!initialFormDataRef.current) return

    const missing = getMissingRequiredFields(formData, 'all')
    if (missing.length > 0) {
      setMissingFields(missing)
      setStatus('Please fill in all required fields', undefined, 'error')
      return
    }

    setMissingFields([])
    const payload = buildEditPatchPayload(formData, initialFormDataRef.current)

    patchVisit(
      { portInfoId: visitId, payload },
      {
        onSuccess: () => {
          setStatus('Visit updated successfully', undefined, 'success')
          onClose()
        },
        onError: () => {
          setStatus('Failed to update visit', undefined, 'error')
        },
      },
    )
  }

  function onDelete() {
    deleteVisit(visitId, {
      onSuccess: () => {
        setStatus('Visit deleted successfully', undefined, 'success')
        onClose()
      },
      onError: () => {
        setStatus('Failed to delete visit', undefined, 'error')
      },
    })
  }

  const isInitialLoading = isLoadingVisit && !visit
  const isLoading = isInitialLoading || isPendingPatch || isPendingDelete

  return (
    <>
      <DialogBox open onClose={requestClose} slotProps={modalSlotProps}>
        <Box
          sx={{
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {isLoading ? (
            <CircularProgress aria-label="Loading…" />
          ) : isErrorPatch ? (
            <Alert severity="error">There was an error saving the visit</Alert>
          ) : (
            <>
              <Box>
                <Typography variant="h3" sx={{ mb: 3 }}>
                  Edit Visit
                </Typography>
                <PageTwo
                  formData={formData}
                  updateFormData={setFormData}
                  portInfoId={visitId}
                  files={visit?.files}
                  missingFields={missingFields}
                />
              </Box>
              {missingFields.length > 0 && (
                <Typography
                  sx={{ color: COLORS.red.mid, textAlign: 'right', mb: 1 }}
                >
                  Some required fields are not filled
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Visit
                </Button>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Button color="error" onClick={requestClose}>
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={onSave}>
                    Save
                  </Button>
                </Box>
              </Box>
              <Typography
                variant="caption"
                sx={{ mt: 3, color: COLORS.greys.dark }}
                component="p"
              >
                Last updated by {visit?.updatedBy} on{' '}
                {formatDate(visit?.updatedAt || '', dateFormat)}
              </Typography>
            </>
          )}
        </Box>
      </DialogBox>

      <UnsavedChangesCloseDialog
        open={showUnsavedCloseConfirm}
        onCancel={() => setShowUnsavedCloseConfirm(false)}
        onConfirmClose={() => {
          setShowUnsavedCloseConfirm(false)
          onClose()
        }}
      />

      <DialogBox
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <Typography variant="h3" sx={{ mb: 3 }}>
          Delete Visit
        </Typography>
        <Typography variant="paragraph1" sx={{ mb: 4 }}>
          Are you sure you want to delete this visit? This action cannot be
          undone.
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'end' }}>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={onDelete}>
            Delete
          </Button>
        </Box>
      </DialogBox>
    </>
  )
}
